if (!customElements.get('fishe-offer-confirmation')) {
  customElements.define('fishe-offer-confirmation', class extends HTMLElement {
    connectedCallback() {
      if (this.controller) return;
      this.controller = new AbortController();
      this.toast ||= this.querySelector('[data-offer-toast]');
      this.inlineLabel = this.querySelector('[data-offer-label]');
      this.activate = this.querySelector('[data-offer-activate]');
      this.heading = this.toast.querySelector('[data-offer-heading]');
      this.description = this.toast.querySelector('[data-offer-description]');
      this.icon = this.toast.querySelector('[data-offer-icon]');
      this.seen = new Set();
      this.shoppingTimers = [];
      this.activationText = this.activate.textContent;
      try {
        const link = new URL(this.dataset.offerLink, location.origin);
        const match = link.pathname.match(/(?:^|\/)discount\/([^/]+)\/?$/);
        if (link.origin !== location.origin || !match) return;
        this.code = decodeURIComponent(match[1]).trim().toUpperCase();
        if (!this.code) return;
      } catch { return; }
      const options = { signal: this.controller.signal };
      this.activate.addEventListener('click', event => {
        if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
        event.preventDefault();
        this.refresh(true);
      }, options);
      this.toast.querySelector('[data-offer-close]').addEventListener('click', () => this.hideToast(), options);
      this.toast.addEventListener('pointerenter', () => clearTimeout(this.dismissTimer), options);
      this.toast.addEventListener('pointerleave', () => this.scheduleDismiss(), options);
      this.toast.addEventListener('focusin', () => clearTimeout(this.dismissTimer), options);
      this.toast.addEventListener('focusout', () => this.scheduleDismiss(), options);
      document.body.append(this.toast);
      window.addEventListener('cart:change', () => {
        this.hideToast();
        this.queueRefresh();
      }, { ...options, capture: true });
      window.addEventListener('cart:refresh', () => this.queueRefresh(), { ...options, capture: true });
      window.addEventListener('variant:add', () => { this.hideToast(); this.queueRefresh(); }, { ...options, capture: true });
      document.addEventListener('submit', event => {
        if (event.target.matches('form[action*="/cart/add"]')) this.checkShoppingUpdate();
      }, { ...options, capture: true });
      document.addEventListener('click', event => {
        const control = event.composedPath().find(node => node instanceof Element && node.matches('button, a'));
        if (control?.closest('[role="dialog"]') || control?.matches('[name="add"], [type="submit"][form], a[href="/cart"]')) {
          this.checkShoppingUpdate();
        }
      }, { ...options, capture: true });
      document.addEventListener('visibilitychange', () => {
        if (document.visibilityState === 'visible') this.queueRefresh();
      }, options);
      window.addEventListener('pageshow', event => {
        if (event.persisted) this.queueRefresh();
      }, options);
      const styles = Array.from(document.querySelectorAll('link[rel="stylesheet"]'))
        .filter(link => /fishe-(promo|campaign|offer-confirmation)\.css/.test(link.href));
      Promise.race([Promise.all(styles.map(link => link.sheet ? Promise.resolve() : new Promise(resolve => {
        link.addEventListener('load', resolve, { ...options, once: true });
        link.addEventListener('error', resolve, { ...options, once: true });
      }))), new Promise(resolve => { this.styleTimer = setTimeout(resolve, 2000); })]).then(() => {
        if (this.controller && !this.controller.signal.aborted) {
          clearTimeout(this.styleTimer);
          this.stylesReady = true;
          this.refresh();
        }
      });
    }

    disconnectedCallback() {
      this.controller?.abort();
      this.controller = null;
      clearTimeout(this.refreshTimer);
      clearTimeout(this.dismissTimer);
      clearTimeout(this.styleTimer);
      this.toast?.remove();
      this.shoppingTimers?.forEach(clearTimeout);
    }

    checkShoppingUpdate() {
      this.hideToast();
      this.shoppingTimers.forEach(clearTimeout);
      this.shoppingTimers = [900, 2500, 6000].map(delay => setTimeout(() => this.refresh(), delay));
    }

    queueRefresh() {
      clearTimeout(this.refreshTimer);
      this.refreshTimer = setTimeout(() => this.refresh(), 200);
    }

    async refresh(activate = false) {
      if (!this.code || !this.stylesReady || !this.controller || this.controller.signal.aborted) return;
      if (this.loading) { this.refreshAgain = true; this.activateAgain ||= activate; return; }
      this.loading = true;
      const signal = this.controller.signal;
      try {
        const root = window.Shopify?.routes?.root || '/';
        const options = {
          credentials: 'same-origin', cache: 'no-store',
          headers: { Accept: 'application/json' },
          signal: AbortSignal.any([signal, AbortSignal.timeout(8000)])
        };
        const response = await fetch(`${root}cart.js`, options);
        if (!response.ok) throw new Error('Cart confirmation unavailable');
        let cart = await response.json();
        const codes = Array.isArray(cart.discount_codes) ? cart.discount_codes : [];
        const hasCode = codes.some(entry => String(entry.code).toUpperCase() === this.code);
        if (!hasCode && (activate || !this.activationAttempted)) {
          this.activationAttempted = true;
          this.dataset.state = 'activating';
          this.inlineLabel.textContent = this.dataset.activatingText;
          this.activate.setAttribute('aria-busy', 'true');
          const discounts = [...new Set([...codes.map(entry => entry.code).filter(Boolean), this.code])];
          const applied = await fetch(`${root}cart/update.js`, {
            ...options, method: 'POST',
            headers: { ...options.headers, 'Content-Type': 'application/json' },
            body: JSON.stringify({ discount: discounts.join(',') })
          });
          if (!applied.ok) throw new Error('Offer activation unavailable');
          await applied.json();
          const verified = await fetch(`${root}cart.js`, { ...options, signal: AbortSignal.any([signal, AbortSignal.timeout(8000)]) });
          if (!verified.ok) throw new Error('Offer verification unavailable');
          cart = await verified.json();
          if (!cart.discount_codes?.some(entry => String(entry.code).toUpperCase() === this.code)) {
            throw new Error('Offer was not saved');
          }
          document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
        }
        if (!signal.aborted) this.renderState(cart, activate);
      } catch {
        if (!signal.aborted) {
          this.dataset.state = 'unverified';
          this.hideToast();
          this.inlineLabel.textContent = this.dataset.activationFailedText;
          this.activate.hidden = false;
        }
      } finally {
        this.loading = false;
        this.activate.removeAttribute('aria-busy');
        if (this.refreshAgain && !signal.aborted) {
          this.refreshAgain = false;
          const retryActivation = this.activateAgain;
          this.activateAgain = false;
          if (retryActivation) this.refresh(true);
          else this.queueRefresh();
        }
      }
    }

    renderState(cart, forceToast = false) {
      const codes = Array.isArray(cart.discount_codes) ? cart.discount_codes : [];
      const offer = codes.find(entry => String(entry.code || '').toUpperCase() === this.code);
      const hasItems = Number(cart.item_count) > 0;
      let state = 'inactive';
      if (offer?.applicable === true && hasItems) state = 'applied';
      else if (offer && !hasItems) state = 'saved';
      else if (offer) state = 'ineligible';
      this.dataset.state = state;
      this.activate.hidden = state === 'applied' || state === 'saved';
      if (state === 'applied') {
        this.inlineLabel.textContent = `✓ ${this.dataset.appliedHeading}`;
        this.heading.textContent = this.dataset.appliedHeading;
        this.description.textContent = this.dataset.offerDetail;
        this.icon.textContent = '✓';
      } else if (state === 'saved') {
        this.inlineLabel.textContent = this.dataset.savedHeading;
        this.heading.textContent = this.dataset.savedHeading;
        this.description.textContent = this.dataset.savedDetail;
        this.icon.textContent = '+';
      } else {
        this.hideToast();
        this.inlineLabel.textContent = state === 'ineligible'
          ? this.dataset.ineligibleText
          : this.dataset.inactiveText;
        return;
      }
      if ((this.seen.size && !forceToast) || this.hasShoppingOverlay()) return;
      this.seen.add(this.code);
      const header = document.querySelector('.shopify-section--header');
      const headerBottom = header?.getBoundingClientRect().bottom || 88;
      this.toast.style.setProperty('--fishe-offer-top', `${Math.max(12, Math.min(headerBottom + 12, 170))}px`);
      this.toast.hidden = false;
      this.scheduleDismiss();
    }

    hasShoppingOverlay() {
      return Array.from(document.querySelectorAll('cart-drawer[open], quick-buy-modal[open], quick-buy-drawer[open], dialog[open], [role="dialog"][aria-modal="true"]'))
        .some(element => element.getClientRects().length && getComputedStyle(element).visibility !== 'hidden');
    }

    scheduleDismiss() {
      clearTimeout(this.dismissTimer);
      if (this.toast.hidden || this.toast.contains(document.activeElement)) return;
      this.dismissTimer = setTimeout(() => this.hideToast(), 9000);
    }

    hideToast() {
      clearTimeout(this.dismissTimer);
      if (this.toast) this.toast.hidden = true;
    }
  });
}
